import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, Users, Building2, Search, Zap, 
  FileText, CheckCircle, File, Trash2, 
  ChevronLeft, ChevronRight, BarChart2, X, Download, Info
} from 'lucide-react';
import axios from 'axios';
import * as XLSX from 'xlsx'; 
import './BeelineJobDetails.css'; 

export default function BeelineJobDetails({ job, onBack }) {
  const [submissions, setSubmissions] = useState([]);
  const [analytics, setAnalytics] = useState([]); 
  const [loading, setLoading] = useState(true);
  
  // --- FILTER STATES ---
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); 

  const [showStatsModal, setShowStatsModal] = useState(false); 
  
  // --- DETAIL MODAL STATES ---
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  useEffect(() => {
    if (job && job._id) {
      fetchData();
    }
  }, [job]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/beeline/job/${job._id}`);
      if (response.data.submissions) {
          setSubmissions(response.data.submissions);
          setAnalytics(response.data.analytics || []);
      } else {
          setSubmissions(response.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // --- HELPER: FORMAT DATE TO MM-DD-YYYY ---
  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    // Handle YYYY-MM-DD string specifically to avoid timezone issues
    if (typeof dateStr === 'string' && dateStr.includes('-')) {
        const parts = dateStr.split('-');
        if (parts.length === 3 && parts[0].length === 4) {
            const [year, month, day] = parts;
            return `${month}-${day}-${year}`;
        }
    }
    // Fallback for other formats
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to permanently delete this submission?")) {
        try {
            await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/beeline/submission/${id}`);
            setSubmissions(prev => prev.filter(sub => sub.id !== id));
            alert("Submission deleted.");
        } catch (error) {
            console.error("Delete failed", error);
            alert("Failed to delete submission.");
        }
    }
  };

  const handleNameClick = (sub) => {
      setSelectedSubmission(sub);
      setShowDetailModal(true);
  };

  const handleExportExcel = () => {
    if (submissions.length === 0) {
        alert("No data to export!");
        return;
    }
    const dataToExport = submissions.map(sub => ({
        "Submitted On": new Date(sub.createdAt).toLocaleDateString(),
        "First Name": sub.firstName,
        "Last Name": sub.lastName,
        "Submitted By": sub.submittedBy || 'Admin',
        "Request ID": sub.requestId,
        "Beeline ID": sub.beelineId,
        "Email": sub.email,
        "Mobile": sub.mobile,
        "DOB": formatDate(sub.dob), // Updated Format
        "Gender": sub.gender,
        "Nationality": sub.nationality,
        "Resource Type": sub.resourceTypeId,
        "Initial Personal ID": sub.initialPersonalId,
        "Full Personal ID": sub.fullPersonalId,
        "LinkedIn": sub.linkedInLink,
        "Total Exp": sub.totalITExperience,
        "Relevant Exp": sub.experienceRequiredSkills,
        "Highest Degree": sub.highestDegree,
        "Specialization": sub.specialization,
        "Recent Company": sub.recentCompany,
        "Company Address": sub.recentCompanyAddress,
        "Edu Start": formatDate(sub.educationStartDate), // Updated Format
        "Edu End": formatDate(sub.educationEndDate), // Updated Format
        "Address": sub.candidateAddress,
        "City": sub.candidateCity,
        "State": sub.candidateState,
        "Pincode": sub.candidatePincode,
        "Submitted Rate": sub.submittedRate,
        "Pay Rate": sub.submittedPayRate,
        "Availability": sub.availability,
        "Date Available": formatDate(sub.dateAvailable),
        "Former TCS Emp": sub.tcsFormerEmployee,
        "Former TCS BA": sub.tcsFormerBusinessAssociate,
        "Original Resume Link": sub.originalResumeUrl || '',
        "Modified Resume Link": sub.modifiedResumeUrl || '',
        "Attestation Link": sub.attestationPdfUrl || ''
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Candidates");
    XLSX.writeFile(workbook, `Beeline_Report_${job.beelineId || 'Export'}.xlsx`);
  };

  const filteredSubmissions = submissions.filter(sub => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = (
        (sub.firstName && sub.firstName.toLowerCase().includes(term)) ||
        (sub.lastName && sub.lastName.toLowerCase().includes(term)) ||
        (sub.email && sub.email.toLowerCase().includes(term)) || 
        (sub.submittedBy && sub.submittedBy.toLowerCase().includes(term))
    );

    let matchesType = true;
    if (filterType === 'vendor') {
        matchesType = !!sub.vendorId; 
    } else if (filterType === 'recruiter') {
        matchesType = !!sub.recruiterId; 
    }

    return matchesSearch && matchesType;
  });

  const totalPages = Math.ceil(filteredSubmissions.length / itemsPerPage);
  const currentData = filteredSubmissions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
        setCurrentPage(newPage);
    }
  };

  // --- UI COMPONENT: DETAIL ROW (Fixed for Overlap) ---
  const DetailRow = ({ label, value }) => (
      <div style={{display:'flex', flexDirection:'column', marginBottom:'12px', minWidth: 0}}>
          <span style={{fontSize:'0.85rem', color:'#64748b', fontWeight:'500'}}>{label}</span>
          <span style={{
              fontSize:'1rem', 
              color:'#1e293b', 
              fontWeight:'600', 
              borderBottom:'1px solid #e2e8f0', 
              paddingBottom:'4px',
              wordBreak: 'break-all' // FIXED: Forces long emails/strings to wrap instead of overlap
          }}>
            {value || '--'}
          </span>
      </div>
  );

  return (
    <div className="beeline-details-container">
      
      {/* --- HEADER (Fixed Layout for Long Titles) --- */}
      <div className="bd-header">
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap: '20px'}}>
            <div style={{flex: 1, minWidth: 0}}> {/* FIXED: Allows text to wrap properly */}
                <button className="bd-back-btn" onClick={() => onBack()}>
                    <ArrowLeft size={18} /> Back to Jobs
                </button>
                <h1 className="bd-role" style={{wordBreak: 'break-word'}}>{job.role}</h1>
                <div className="bd-company">
                    <Building2 size={16} /> {job.companyName}
                </div>
            </div>
            
            <div className="bd-meta-bar" style={{flexShrink: 0}}> {/* FIXED: Prevents buttons from squashing */}
                <div className="bd-meta-item">
                    <Users size={16} color="#64748b"/>
                    <span>Assigned: <strong>{job.assignedTo ? job.assignedTo.length : 0}</strong></span>
                </div>
                <div className="bd-meta-item">
                    <Zap size={16} color="#f59e0b"/>
                    <span>Total: <strong>{submissions.length}</strong></span>
                </div>
                
                <button 
                    className="bd-stats-btn" 
                    onClick={() => setShowStatsModal(true)}
                    style={{
                        marginLeft:'10px', padding:'6px 12px', background:'#e0f2fe', color:'#0284c7', 
                        border:'1px solid #bae6fd', borderRadius:'6px', cursor:'pointer', display:'flex', alignItems:'center', gap:'5px', fontWeight:'600'
                    }}
                >
                    <BarChart2 size={16}/> View Breakdown
                </button>

                <button 
                    className="bd-export-btn" 
                    onClick={handleExportExcel}
                    style={{
                        marginLeft:'8px', padding:'6px 12px', background:'#dcfce7', color:'#166534', 
                        border:'1px solid #bbf7d0', borderRadius:'6px', cursor:'pointer', display:'flex', alignItems:'center', gap:'5px', fontWeight:'600'
                    }}
                >
                    <Download size={16}/> Export Excel
                </button>
            </div>
        </div>
      </div>

      {/* --- TOOLBAR --- */}
      <div className="bd-toolbar">
        <div className="bd-search">
            <Search size={18} color="#94a3b8"/>
            <input 
                type="text" 
                placeholder="Search candidate, vendor..." 
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
        </div>

        <div className="bd-filter-group">
            <button className={`filter-btn ${filterType === 'all' ? 'active' : ''}`} onClick={() => { setFilterType('all'); setCurrentPage(1); }}>All Candidates</button>
            <button className={`filter-btn ${filterType === 'vendor' ? 'active' : ''}`} onClick={() => { setFilterType('vendor'); setCurrentPage(1); }}>Vendor Submitted</button>
            <button className={`filter-btn ${filterType === 'recruiter' ? 'active' : ''}`} onClick={() => { setFilterType('recruiter'); setCurrentPage(1); }}>Recruiter Submitted</button>
        </div>
      </div>

      {/* --- SCROLLABLE TABLE --- */}
      <div className="bd-table-wrapper table-container" style={{ overflowX: 'auto', position: 'relative' }}>
        <table className="modern-table" style={{ minWidth: '3500px' }}>
            <thead>
                <tr>
                    <th style={{width:'120px', position:'sticky', left:0, zIndex:20, backgroundColor:'#f8fafc', borderRight:'1px solid #e2e8f0'}}>Submitted On</th>
                    <th style={{width:'200px', position:'sticky', left:'120px', zIndex:20, backgroundColor:'#f8fafc', borderRight:'2px solid #e2e8f0', boxShadow:'4px 0 5px -2px rgba(0,0,0,0.1)'}}>Candidate Name</th>
                    <th style={{width:'220px', backgroundColor:'#f0f9ff'}}>Submitted By</th>
                    <th style={{width:'150px'}}>Request ID</th>
                    <th style={{width:'150px'}}>Beeline ID</th>
                    <th style={{width:'220px'}}>Email</th>
                    <th style={{width:'140px'}}>Mobile</th>
                    <th style={{width:'100px'}}>DOB</th>
                    <th style={{width:'100px'}}>Gender</th>
                    <th style={{width:'120px'}}>Nationality</th>
                    <th style={{width:'150px'}}>Resource Type</th>
                    <th style={{width:'150px'}}>Initial ID</th>
                    <th style={{width:'150px'}}>Full Personal ID</th>
                    <th style={{width:'200px'}}>LinkedIn</th>
                    <th style={{width:'100px'}}>Total Exp</th>
                    <th style={{width:'100px'}}>Req Skill Exp</th>
                    <th style={{width:'150px'}}>Highest Degree</th>
                    <th style={{width:'150px'}}>Specialization</th>
                    <th style={{width:'180px'}}>Recent Company</th>
                    <th style={{width:'200px'}}>Company Address</th>
                    <th style={{width:'120px'}}>Edu Start</th>
                    <th style={{width:'120px'}}>Edu End</th>
                    <th style={{width:'200px'}}>Cand Address</th>
                    <th style={{width:'120px'}}>City</th>
                    <th style={{width:'120px'}}>State</th>
                    <th style={{width:'100px'}}>Pincode</th>
                    <th style={{width:'120px'}}>Sub. Rate</th>
                    <th style={{width:'120px'}}>Pay Rate</th>
                    <th style={{width:'100px'}}>Available?</th>
                    <th style={{width:'120px'}}>Date Avail</th>
                    <th style={{width:'120px'}}>Former TCS?</th>
                    <th style={{width:'120px'}}>Former BA?</th>
                    <th style={{width:'200px'}}>Attachments</th>
                    <th style={{width:'80px', textAlign:'center', position:'sticky', right:0, zIndex:10, backgroundColor:'#f8fafc', borderLeft:'1px solid #e2e8f0'}}>Action</th>
                </tr>
            </thead>
            <tbody>
                {loading ? (
                    <tr><td colSpan="40" style={{textAlign: 'center', padding: '40px'}}>Loading submissions...</td></tr>
                ) : currentData.length === 0 ? (
                    <tr><td colSpan="40" style={{textAlign: 'center', padding: '40px', color: '#94a3b8'}}>No {filterType !== 'all' ? filterType : ''} submissions found matching your search.</td></tr>
                ) : (
                    currentData.map(sub => (
                        <tr key={sub.id}>
                            <td style={{fontSize:'0.85rem', color:'#64748b', position:'sticky', left:0, backgroundColor:'white', zIndex:10, borderRight:'1px solid #e2e8f0'}}>
                                {new Date(sub.createdAt).toLocaleDateString()}
                            </td>
                            {/* --- CLICKABLE CANDIDATE NAME --- */}
                            <td 
                                onClick={() => handleNameClick(sub)}
                                style={{
                                    fontWeight: '700', color:'#2563eb', cursor: 'pointer', textDecoration:'underline',
                                    position:'sticky', left:'120px', backgroundColor:'white', zIndex:10, 
                                    borderRight:'2px solid #e2e8f0', boxShadow:'4px 0 5px -2px rgba(0,0,0,0.1)'
                                }}
                                title="Click to view full details"
                            >
                                {sub.firstName} {sub.lastName}
                            </td>
                            <td style={{fontSize:'0.9rem', color:'#334155', fontWeight:'500', backgroundColor:'#f0f9ff'}}>
                                {sub.submittedBy || 'Admin'}
                            </td>
                            <td>{sub.requestId}</td>
                            <td>{sub.beelineId}</td>
                            <td>{sub.email}</td>
                            <td>{sub.mobile}</td>
                            <td>{formatDate(sub.dob)}</td> {/* Updated Format */}
                            <td>{sub.gender}</td>
                            <td>{sub.nationality}</td>
                            <td>{sub.resourceTypeId}</td>
                            <td>{sub.initialPersonalId}</td>
                            <td>{sub.fullPersonalId}</td>
                            <td>{sub.linkedInLink ? <a href={sub.linkedInLink} target="_blank" rel="noreferrer">Profile</a> : '--'}</td>
                            <td>{sub.totalITExperience}</td>
                            <td>{sub.experienceRequiredSkills}</td>
                            <td>{sub.highestDegree}</td>
                            <td>{sub.specialization}</td>
                            <td>{sub.recentCompany}</td>
                            <td>{sub.recentCompanyAddress}</td>
                            <td>{formatDate(sub.educationStartDate)}</td> {/* Updated Format */}
                            <td>{formatDate(sub.educationEndDate)}</td> {/* Updated Format */}
                            <td>{sub.candidateAddress}</td>
                            <td>{sub.candidateCity}</td>
                            <td>{sub.candidateState}</td>
                            <td>{sub.candidatePincode}</td>
                            <td style={{color:'#059669', fontWeight:'600'}}>${sub.submittedRate}</td>
                            <td>${sub.submittedPayRate}</td>
                            <td>{sub.availability}</td>
                            <td>{formatDate(sub.dateAvailable)}</td>
                            <td>{sub.tcsFormerEmployee}</td>
                            <td>{sub.tcsFormerBusinessAssociate}</td>
                            <td>
                                <div style={{display:'flex', gap:'12px'}}>
                                    {sub.originalResumeUrl && (<a href={sub.originalResumeUrl} target="_blank" rel="noreferrer" title="Original Resume"><FileText size={18} color="#64748b" style={{cursor:'pointer'}} /></a>)}
                                    {sub.modifiedResumeUrl && (<a href={sub.modifiedResumeUrl} target="_blank" rel="noreferrer" title="Modified Resume"><File size={18} color="#2563eb" style={{cursor:'pointer'}} /></a>)}
                                    {sub.attestationPdfUrl && (<a href={sub.attestationPdfUrl} target="_blank" rel="noreferrer" title="Pontoon Attestation"><CheckCircle size={18} color="#059669" style={{cursor:'pointer'}} /></a>)}
                                </div>
                            </td>
                            <td style={{textAlign:'center', position:'sticky', right:0, backgroundColor:'white', zIndex:10, borderLeft:'1px solid #e2e8f0'}}>
                                <button onClick={() => handleDelete(sub.id)} className="action-btn-delete" title="Delete Submission" style={{border:'none', background:'none', cursor:'pointer', color:'#dc2626'}}>
                                    <Trash2 size={18} />
                                </button>
                            </td>
                        </tr>
                    ))
                )}
            </tbody>
        </table>
      </div>

      {/* --- PAGINATION --- */}
      {totalPages > 1 && (
        <div className="bd-pagination">
            <button disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)} className="page-btn"><ChevronLeft size={16} /> Prev</button>
            <span className="page-info">Page {currentPage} of {totalPages}</span>
            <button disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)} className="page-btn">Next <ChevronRight size={16} /></button>
        </div>
      )}

      {/* --- STATS MODAL --- */}
      {showStatsModal && (
        <div className="bd-modal-overlay">
            <div className="bd-modal-content">
                <div className="bd-modal-header">
                    <h2>Partner Submission Breakdown</h2>
                    <button onClick={() => setShowStatsModal(false)} className="bd-close-btn"><X size={24}/></button>
                </div>
                <div className="bd-modal-body">
                    {analytics.length === 0 ? (
                        <p style={{textAlign:'center', color:'#94a3b8', padding:'20px'}}>No partners assigned or data unavailable.</p>
                    ) : (
                        <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
                            <div style={{display:'grid', gridTemplateColumns:'2fr 1fr 1fr', padding:'8px', background:'#f8fafc', fontWeight:'600', color:'#475569', borderRadius:'6px'}}>
                                <span>Partner Name</span>
                                <span>Type</span>
                                <span style={{textAlign:'right'}}>Submissions</span>
                            </div>
                            {analytics.map((item) => (
                                <div key={item.id} style={{display:'grid', gridTemplateColumns:'2fr 1fr 1fr', padding:'10px', borderBottom:'1px solid #f1f5f9', alignItems:'center'}}>
                                    <span style={{fontWeight:'500', color:'#334155'}}>{item.name}</span>
                                    <span style={{fontSize:'0.85rem', color:'#64748b'}}>{item.type}</span>
                                    <span style={{textAlign:'right', fontWeight:'700', color: item.count > 0 ? '#059669' : '#cbd5e1'}}>{item.count}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
      )}

      {/* --- CANDIDATE DETAIL MODAL --- */}
      {showDetailModal && selectedSubmission && (
        <div className="bd-modal-overlay">
            <div className="bd-modal-content" style={{ maxWidth: '900px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
                <div className="bd-modal-header" style={{borderBottom:'1px solid #e2e8f0', paddingBottom:'15px', marginBottom:'20px'}}>
                    <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                        <Info size={24} color="#2563eb" />
                        <h2 style={{margin:0, fontSize:'1.5rem', color:'#1e293b'}}>Candidate Full Details</h2>
                    </div>
                    <button onClick={() => setShowDetailModal(false)} className="bd-close-btn"><X size={24}/></button>
                </div>
                
                <div className="bd-modal-body">
                    {/* SECTION 1: IDENTITY */}
                    <h3 style={{fontSize:'1.1rem', color:'#2563eb', marginBottom:'15px', borderBottom:'2px solid #e0f2fe', paddingBottom:'5px', marginTop:'0'}}>1. Identity & Personal Details</h3>
                    <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:'20px', marginBottom:'30px'}}>
                        <DetailRow label="First Name" value={selectedSubmission.firstName} />
                        <DetailRow label="Last Name" value={selectedSubmission.lastName} />
                        <DetailRow label="Email" value={selectedSubmission.email} />
                        <DetailRow label="Mobile" value={selectedSubmission.mobile} />
                        <DetailRow label="Request ID" value={selectedSubmission.requestId} />
                        <DetailRow label="Beeline ID" value={selectedSubmission.beelineId} />
                        <DetailRow label="DOB" value={formatDate(selectedSubmission.dob)} />
                        <DetailRow label="Initial Personal ID" value={selectedSubmission.initialPersonalId} />
                        <DetailRow label="Full Personal ID" value={selectedSubmission.fullPersonalId} />
                        <DetailRow label="Gender" value={selectedSubmission.gender} />
                        <DetailRow label="Nationality" value={selectedSubmission.nationality} />
                        <DetailRow label="LinkedIn" value={selectedSubmission.linkedInLink} />
                        <DetailRow label="Former TCS Emp?" value={selectedSubmission.tcsFormerEmployee} />
                        <DetailRow label="Former TCS BA?" value={selectedSubmission.tcsFormerBusinessAssociate} />
                        <DetailRow label="Availability" value={selectedSubmission.availability} />
                        <DetailRow label="Date Available" value={formatDate(selectedSubmission.dateAvailable)} />
                    </div>

                    {/* SECTION 2: ADDITIONAL INFO */}
                    <h3 style={{fontSize:'1.1rem', color:'#2563eb', marginBottom:'15px', borderBottom:'2px solid #e0f2fe', paddingBottom:'5px'}}>2. Additional Information</h3>
                    <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:'20px', marginBottom:'30px'}}>
                        <DetailRow label="Total IT Experience" value={selectedSubmission.totalITExperience} />
                        <DetailRow label="Req Skills Experience" value={selectedSubmission.experienceRequiredSkills} />
                        <DetailRow label="Highest Degree" value={selectedSubmission.highestDegree} />
                        <DetailRow label="Specialization" value={selectedSubmission.specialization} />
                        <DetailRow label="Edu Start Date" value={formatDate(selectedSubmission.educationStartDate)} />
                        <DetailRow label="Edu End Date" value={formatDate(selectedSubmission.educationEndDate)} />
                        <DetailRow label="Recent Company" value={selectedSubmission.recentCompany} />
                        <DetailRow label="Company Address" value={selectedSubmission.recentCompanyAddress} />
                        <DetailRow label="Candidate Address" value={selectedSubmission.candidateAddress} />
                        <DetailRow label="City" value={selectedSubmission.candidateCity} />
                        <DetailRow label="State" value={selectedSubmission.candidateState} />
                        <DetailRow label="Pincode" value={selectedSubmission.candidatePincode} />
                    </div>

                    {/* SECTION 3: RATES & INFO */}
                    <h3 style={{fontSize:'1.1rem', color:'#2563eb', marginBottom:'15px', borderBottom:'2px solid #e0f2fe', paddingBottom:'5px'}}>3. Candidate Info & Rates</h3>
                    <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:'20px', marginBottom:'30px'}}>
                        <DetailRow label="Resource Type ID" value={selectedSubmission.resourceTypeId} />
                        <DetailRow label="Submitted Rate" value={selectedSubmission.submittedRate ? `$${selectedSubmission.submittedRate}` : '--'} />
                        <DetailRow label="Pay Rate" value={selectedSubmission.submittedPayRate ? `$${selectedSubmission.submittedPayRate}` : '--'} />
                    </div>

                    {/* SECTION 4: ATTACHMENTS (NEW) */}
                    <h3 style={{fontSize:'1.1rem', color:'#2563eb', marginBottom:'15px', borderBottom:'2px solid #e0f2fe', paddingBottom:'5px'}}>4. Attachments</h3>
                    <div style={{display:'flex', gap:'15px', paddingBottom:'20px', flexWrap:'wrap'}}>
                        {selectedSubmission.originalResumeUrl ? (
                            <a 
                                href={selectedSubmission.originalResumeUrl} 
                                target="_blank" 
                                rel="noreferrer" 
                                style={{
                                    display:'flex', alignItems:'center', gap:'8px', padding:'10px 16px', 
                                    border:'1px solid #cbd5e1', borderRadius:'8px', textDecoration:'none', 
                                    color:'#334155', fontWeight:'500', background:'#f8fafc', transition:'all 0.2s'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.borderColor = '#2563eb'}
                                onMouseOut={(e) => e.currentTarget.style.borderColor = '#cbd5e1'}
                            >
                                <FileText size={18} color="#64748b" /> Original Resume
                            </a>
                        ) : <span style={{color:'#94a3b8', fontStyle:'italic'}}>No Original Resume</span>}

                        {selectedSubmission.modifiedResumeUrl && (
                            <a 
                                href={selectedSubmission.modifiedResumeUrl} 
                                target="_blank" 
                                rel="noreferrer" 
                                style={{
                                    display:'flex', alignItems:'center', gap:'8px', padding:'10px 16px', 
                                    border:'1px solid #93c5fd', borderRadius:'8px', textDecoration:'none', 
                                    color:'#1e40af', fontWeight:'500', background:'#eff6ff', transition:'all 0.2s'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.background = '#dbeafe'}
                                onMouseOut={(e) => e.currentTarget.style.background = '#eff6ff'}
                            >
                                <File size={18} color="#2563eb" /> Modified Resume
                            </a>
                        )}

                        {selectedSubmission.attestationPdfUrl && (
                            <a 
                                href={selectedSubmission.attestationPdfUrl} 
                                target="_blank" 
                                rel="noreferrer" 
                                style={{
                                    display:'flex', alignItems:'center', gap:'8px', padding:'10px 16px', 
                                    border:'1px solid #86efac', borderRadius:'8px', textDecoration:'none', 
                                    color:'#166534', fontWeight:'500', background:'#f0fdf4', transition:'all 0.2s'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.background = '#dcfce7'}
                                onMouseOut={(e) => e.currentTarget.style.background = '#f0fdf4'}
                            >
                                <CheckCircle size={18} color="#16a34a" /> Attestation PDF
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </div>
      )}

    </div>
  );
}
